import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme, THEMES, FONT_SIZES } from '../context/ThemeContext';
import './Navbar.css';

// Routes that live under the Planning Tools dropdown — the trigger
// button highlights when the user is on any of them.
const PLANNING_ROUTES = ['/estimator', '/guided-planner', '/detailed-planner'];

export default function Navbar() {
  const location = useLocation();
  const { theme, fontSize, setTheme, setFontSize } = useTheme();

  const [toolsOpen, setToolsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const toolsRef = useRef(null);
  const settingsRef = useRef(null);

  // Close the Planning Tools dropdown on outside-click / Escape
  useEffect(() => {
    if (!toolsOpen) return;
    function onClick(e) {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) {
        setToolsOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setToolsOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [toolsOpen]);

  // Close the settings dropdown on outside-click / Escape
  useEffect(() => {
    if (!settingsOpen) return;
    function onClick(e) {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setSettingsOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [settingsOpen]);

  const isActive = (path) =>
    location.pathname === path ? 'nav-link active' : 'nav-link';

  const planningActive = PLANNING_ROUTES.includes(location.pathname);
  const activeTheme = THEMES.find((t) => t.id === theme);

  // Close the dropdown after a tool link is clicked
  const closeTools = () => setToolsOpen(false);

  return (
    <nav className="navbar" aria-label="Main navigation">
      <Link to="/" className="navbar-brand">
        <span className="brand-icon" aria-hidden="true">◈</span>
        <span className="brand-text">CanadianRetirePlan</span>
      </Link>

      <div className="navbar-links">
        {/* ── Planning Tools dropdown ── */}
        <div className="nav-dropdown" ref={toolsRef}>
          <button
            type="button"
            className={`nav-link nav-dropdown-trigger ${planningActive ? 'active' : ''} ${toolsOpen ? 'is-open' : ''}`}
            aria-expanded={toolsOpen}
            aria-haspopup="true"
            onClick={() => setToolsOpen((o) => !o)}
          >
            Planning Tools
            <svg
              className={`nav-chevron ${toolsOpen ? 'rotated' : ''}`}
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="2,3.5 5,6.5 8,3.5" />
            </svg>
          </button>

          {toolsOpen && (
            <div className="nav-dropdown-panel" role="menu">
              <Link
                to="/estimator"
                role="menuitem"
                className={`nav-dropdown-item ${location.pathname === '/estimator' ? 'is-active' : ''}`}
                onClick={closeTools}
              >
                Estimator
              </Link>
              <Link
                to="/guided-planner"
                role="menuitem"
                className={`nav-dropdown-item ${location.pathname === '/guided-planner' ? 'is-active' : ''}`}
                onClick={closeTools}
              >
                Guided Planner
              </Link>
              <Link
                to="/detailed-planner"
                role="menuitem"
                className={`nav-dropdown-item ${location.pathname === '/detailed-planner' ? 'is-active' : ''}`}
                onClick={closeTools}
              >
                Detailed Planner
              </Link>
            </div>
          )}
        </div>

        {/* ── Blogs (route to be wired up later) ── */}
        <Link to="/blogs" className={isActive('/blogs')}>Blogs</Link>

        {/* ── Video Library ── */}
        <Link to="/videos" className={isActive('/videos')}>Video Library</Link>
      </div>

      <div className="navbar-right">
        <div className="settings-menu" ref={settingsRef}>
          <button
            type="button"
            className="settings-trigger"
            aria-label="Display settings"
            aria-expanded={settingsOpen}
            aria-haspopup="true"
            onClick={() => setSettingsOpen((o) => !o)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                 strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>

          {settingsOpen && (
            <div className="settings-panel" role="menu">
              {/* Theme */}
              <div className="settings-section">
                <div className="settings-row">
                  <div className="settings-label">Theme</div>
                  <div className="settings-current">{activeTheme?.name}</div>
                </div>
                <div className="theme-grid" role="radiogroup" aria-label="Theme">
                  {THEMES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      role="radio"
                      aria-checked={theme === t.id}
                      title={`${t.name} — ${t.desc}`}
                      aria-label={`Apply ${t.name} theme`}
                      className={`theme-swatch ${theme === t.id ? 'is-active' : ''}`}
                      style={{ '--sw-1': t.sw1, '--sw-2': t.sw2 }}
                      onClick={() => setTheme(t.id)}
                    >
                      <span aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Font size */}
              <div className="settings-section">
                <div className="settings-row">
                  <div className="settings-label">Text size</div>
                </div>
                <div className="seg-group" role="radiogroup" aria-label="Text size">
                  {FONT_SIZES.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      role="radio"
                      aria-checked={fontSize === f.id}
                      aria-label={f.desc}
                      title={f.desc}
                      className={`seg-btn seg-btn-fs seg-btn-fs-${f.id} ${fontSize === f.id ? 'is-active' : ''}`}
                      onClick={() => setFontSize(f.id)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}